import { ReactNode } from 'react';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Zap, Clock, Mic, Loader2 } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';

interface ExpiredTrialGateProps {
  children: ReactNode;
}

// Pages that should still be accessible even with expired trial
const ALLOWED_PATHS = ['/plano', '/perfil', '/admin'];

export function ExpiredTrialGate({ children }: ExpiredTrialGateProps) {
  const { isLoading, isError, isTrial, isExpired, hasSubscription, expirationReason } = useTrialStatus();
  const location = useLocation();
  const navigate = useNavigate();

  // Allow access to specific pages regardless of trial status
  if (ALLOWED_PATHS.includes(location.pathname)) {
    return <>{children}</>;
  }

  // Still loading trial status
  if (isLoading) {
    return <>{children}</>;
  }

  // If there was an error checking trial status, fail-open (don't block the user)
  if (isError) {
    return <>{children}</>;
  }

  // No subscription at all — block
  const shouldBlock = !hasSubscription || (isTrial && isExpired);

  if (!shouldBlock) {
    return <>{children}</>;
  }

  // Show blocking screen inside AppLayout
  return (
    <AppLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-lg w-full text-center space-y-6 p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              {!hasSubscription ? 'Sem Assinatura Ativa' : 'Trial Expirado'}
            </h1>
            <p className="text-muted-foreground">
              {!hasSubscription
                ? 'Você não possui uma assinatura ativa. Escolha um plano para começar a usar o ProntuÁudio.'
                : expirationReason === 'time'
                  ? 'Seu período de teste de 14 dias terminou. Para continuar usando o ProntuÁudio, escolha um plano.'
                  : expirationReason === 'minutes'
                    ? 'Você utilizou todos os minutos do seu trial. Para continuar, escolha um plano.'
                    : 'Seu período de teste expirou. Escolha um plano para continuar.'}
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-left">
            <p className="text-sm font-medium text-foreground">Com um plano pago você tem:</p>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-center gap-2">
                <Mic className="h-4 w-4 text-primary flex-shrink-0" />
                Gravações ilimitadas dentro do seu plano
              </li>
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary flex-shrink-0" />
                Até 2.500 minutos mensais de transcrição
              </li>
              <li className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary flex-shrink-0" />
                Processamento com IA avançada
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              className="w-full gradient-copper text-white"
              onClick={() => navigate('/plano')}
            >
              <Zap className="h-5 w-5 mr-2" />
              Escolher um Plano
            </Button>
            <p className="text-xs text-muted-foreground">
              Seus dados estão seguros e serão mantidos ao ativar um plano.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
